<script setup lang="ts">
/**
 * The grid a new table's size is picked from — „Tabelle einfügen“ in the rich
 * text toolbar and in Write's ribbon. Pointer or focus on a cell shows that
 * many rows × columns (the label below reads it out), a click picks it.
 *
 * `dimensions` words the size („3 × 2-Tabelle“): the cells' accessible names
 * and the label; the host has the translation.
 */
import { ref } from 'vue';

export interface NxTableSize {
  rows: number;
  columns: number;
}

const props = withDefaults(
  defineProps<{
    /** `{rows}` and `{columns}` of a size as text. */
    dimensions: (size: NxTableSize) => string;
    /** Cells per side. Defaults to 5. */
    size?: number;
  }>(),
  { size: 5 }
);

const emit = defineEmits<{ pick: [size: NxTableSize] }>();

const hover = ref<NxTableSize>({ rows: 1, columns: 1 });
const cellOf = (n: number): NxTableSize => ({
  rows: Math.ceil(n / props.size),
  columns: ((n - 1) % props.size) + 1,
});

function pick(n: number): void {
  hover.value = cellOf(n);
  emit('pick', cellOf(n));
}
</script>

<template>
  <div class="nx-table-size-grid">
    <div
      class="nx-table-size-grid__cells"
      :style="{ gridTemplateColumns: `repeat(${props.size}, var(--nxt-table-size-grid--cell))` }"
    >
      <button
        v-for="n in props.size * props.size"
        :key="n"
        type="button"
        class="nx-table-size-grid__cell"
        :class="{
          'is-on': cellOf(n).rows <= hover.rows && cellOf(n).columns <= hover.columns,
        }"
        :aria-label="props.dimensions(cellOf(n))"
        @mouseenter="hover = cellOf(n)"
        @focus="hover = cellOf(n)"
        @click="pick(n)"
      />
    </div>
    <div class="nx-table-size-grid__label">{{ props.dimensions(hover) }}</div>
  </div>
</template>

<style scoped>
.nx-table-size-grid {
  --nxt-table-size-grid--cell: calc(var(--nxt-size-unit) * 1.125);
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
}

.nx-table-size-grid__cells {
  display: grid;
  gap: var(--nxt-space-2xs);
}

.nx-table-size-grid__cell {
  width: var(--nxt-table-size-grid--cell);
  height: var(--nxt-table-size-grid--cell);
  padding: 0;
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-sm);
  background: var(--nxt-surface-bg);
  cursor: pointer;
}

.nx-table-size-grid__cell.is-on {
  border-color: var(--nxt-brand-bg);
  background: var(--nxt-brand-subtle-bg);
}

.nx-table-size-grid__cell:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: var(--nxt-hairline);
}

.nx-table-size-grid__label {
  color: var(--nxt-fg-subtle);
  font-size: var(--nxt-font-size-xs);
  text-align: center;
}
</style>
